import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import _ from 'lodash'
import { getModelUrl, getVehiclesYears, getPathAlias } from 'tw-oi-core/utils/vehicle'
import {
  setVehicleProgram,
  getVehicleProgram,
  trackChangeVehicleClick,
  setYearDimension,
  setTypeSubType,
  setBrand,
} from 'tw-oi-core/services/analytics'

import UnifiedHeader from '../components/UnifiedHeader'
import ErrorMessage from '../components/ErrorMessage'
import VehicleYearSelector from '../components/VehicleYearSelector'
import VehicleList from '../components/VehicleList'
import VehicleModelSearch from '../components/VehicleModelSearch'
import BrandPillFilter from '../components/BrandPillFilter'
import Loader from '../components/Loader'
import StickyBar from '../components/StickyBar'
import Media from '../components/Media'
import * as VehicleActions from 'tw-oi-core/actions/VehicleActions'
import * as ContentsActions from 'tw-oi-core/actions/ContentsActions'
import {
  groupedVehiclesByYearAndProductType,
  getVehiclesForDivision,
  filterVehiclesByBrand,
  searchVehiclesByQuery,
  getUniqueBrandsFromVehicles,
  isLegacyYear,
} from '../extras/utils'
import {
  BRANDS,
  DIVISIONS,
  MESSAGE,
  PRODUCT_MAP,
  PRODUCT_SUBTYPE_MAP,
  ROUTE,
  UI,
} from '../config'

import { clearLastVehicle } from '../extras/cookies'
import { navigateUnifiedHeaderLogo, buildDivisionMobileDropdown } from '../extras/unifiedHeaderNavigation'
import { isOffline } from '../services/offlineDetect'

class DivisionVehicles extends Component {
  static propTypes = {
    match: PropTypes.shape({
      params: PropTypes.shape({
        divisionKey: PropTypes.string.isRequired,
        year: PropTypes.string,
        group: PropTypes.string,
      }).isRequired,
    }).isRequired,
    history: PropTypes.shape({
      replace: PropTypes.func.isRequired,
      push: PropTypes.func.isRequired,
    }).isRequired,
    vehicle: PropTypes.object.isRequired,
    VehicleActions: PropTypes.object.isRequired,
    ContentsActions: PropTypes.object.isRequired,
    heroShotMap: PropTypes.object,
    isDesktop: PropTypes.bool,
  }

  constructor(props) {
    super(props)

    this.state = {
      models: [],
      groups: [],
      years: [],
      vehiclesReady: false,
      vehiclesMap: {},
      brandFilter: null,
      searchQuery: '',
    }
  }

  get division() {
    return DIVISIONS.find(item => item.key === this.props.match.params.divisionKey)
  }

  get baseRoute() {
    return `${ROUTE.DIVISIONS}/${this.props.match.params.divisionKey}`
  }

  componentDidMount() {
    clearLastVehicle()

    const { getVehicles } = this.props.VehicleActions
    const { vehicles } = this.props.vehicle

    if (_.isEmpty(vehicles)) {
      getVehicles().then(() => this.updateVehiclesList())
    } else {
      this.updateVehiclesList()
    }
  }

  componentDidUpdate(prevProps) {
    const { match: { params: { year, group, divisionKey } } } = this.props
    const { match: { params: { year: prevYear, group: prevGroup, divisionKey: prevDivisionKey } } } = prevProps
    const { vehiclesReady } = this.state

    if (!vehiclesReady) return

    if (divisionKey !== prevDivisionKey || prevYear !== year || prevGroup !== group) {
      this.setState({ brandFilter: null, searchQuery: '' })
      this.updateVehiclesList()
    }
  }

  resolveProductType(group) {
    return _.isEmpty(group) ? null : _.toUpper(group)
  }

  updateVehiclesList() {
    const division = this.division
    if (!division) return

    const { match: { params: { year, group } }, history, VehicleActions: { setVehicleProductType, setVehicleYear } } = this.props
    const { vehicles, currentYear, currentProductType } = this.props.vehicle
    const newProductType = this.resolveProductType(group)
    const divisionVehicles = getVehiclesForDivision(vehicles, division)
    const vehiclesMap = groupedVehiclesByYearAndProductType(divisionVehicles)
    const years = getVehiclesYears(divisionVehicles).filter(y => y)
    const activeYear = year || years[0]
    const groups = Object.keys(vehiclesMap[activeYear] || {}).sort()
    const models = ((newProductType && vehiclesMap[activeYear] && vehiclesMap[activeYear][newProductType]) || [])
      .sort((a, b) => a.model.localeCompare(b.model))

    if (activeYear && activeYear !== currentYear) {
      setVehicleYear(activeYear)
    }
    if (newProductType !== currentProductType) {
      setVehicleProductType(newProductType, groups)
    }

    this.setState({ models, groups, vehiclesMap, years, vehiclesReady: true })

    if (!year && !_.isEmpty(years)) {
      history.replace(`${this.baseRoute}${ROUTE.VEHICLES}/${years[0]}`)
      return
    }

    if (newProductType && groups.indexOf(newProductType) === -1) {
      history.replace(`${this.baseRoute}${ROUTE.VEHICLES}/${activeYear}`)
      return
    }

    if (!isLegacyYear(activeYear) && activeYear && groups && groups.length === 1 && !newProductType) {
      history.replace(`${this.baseRoute}${ROUTE.VEHICLES}/${activeYear}/${getPathAlias(groups[0])}`)
      return
    }

    if (isLegacyYear(activeYear) && models && models.length === 1 && models[0].year === activeYear && !models[0].FOREST_RIVER_PRODUCT_SUB_TYPE) {
      this.navigateToVehicle(models[0])
    }
  }

  getModelLink = (vehicle) => {
    const brand = BRANDS.find(item => item.name === vehicle.make)
    if (!brand) return ROUTE.INDEX

    return `/${brand.key}${ROUTE.GUIDE}${getModelUrl(vehicle.year, `${vehicle.id} ${vehicle.model}`)}${ROUTE.BROWSE}`
  }

  navigateToVehicle = (vehicle) => {
    const { setVehicleBrand } = this.props.VehicleActions
    setVehicleBrand(vehicle.make)
    this.updateAnalyticsContext(vehicle)
    this.props.history.push(this.getModelLink(vehicle))
  }

  updateAnalyticsContext(vehicle) {
    const { model, make, year, FOREST_RIVER_PRODUCT_TYPE, FOREST_RIVER_PRODUCT_SUB_TYPE } = vehicle
    const vehicleProgramStringBefore = getVehicleProgram()
    const vehicleProgramStringAfter = `${year} ${make} ${model}`

    setVehicleProgram(year, make, model)
    setYearDimension(year)
    setBrand(make)

    const selectedProductType = PRODUCT_MAP[FOREST_RIVER_PRODUCT_TYPE]
      ? PRODUCT_MAP[FOREST_RIVER_PRODUCT_TYPE].title
      : FOREST_RIVER_PRODUCT_TYPE
    const selectedProductSubtype = PRODUCT_SUBTYPE_MAP[FOREST_RIVER_PRODUCT_SUB_TYPE] || FOREST_RIVER_PRODUCT_SUB_TYPE
    setTypeSubType(selectedProductType, selectedProductSubtype)
    trackChangeVehicleClick(vehicleProgramStringBefore, vehicleProgramStringAfter)
  }

  getSearchPool() {
    const { vehiclesMap, years } = this.state
    const { match: { params: { year } } } = this.props
    const activeYear = year || years[0]

    if (!activeYear || !vehiclesMap[activeYear]) return []

    return Object.keys(vehiclesMap[activeYear]).reduce((acc, productType) => {
      return acc.concat(vehiclesMap[activeYear][productType] || [])
    }, [])
  }

  getDisplayModels() {
    const { models, brandFilter, searchQuery } = this.state
    let displayModels = models

    displayModels = filterVehiclesByBrand(displayModels, brandFilter)
    displayModels = searchVehiclesByQuery(displayModels, searchQuery)

    return displayModels
  }

  handleSelectCategoryClick = () => {
    clearLastVehicle()
  }

  renderBreadcrumb(division, productType) {
    const { match: { params: { year } } } = this.props
    const divisionPath = `${this.baseRoute}${ROUTE.VEHICLES}/${year || ''}`

    if (!productType) {
      return (
        <>
          <Link
            to={ROUTE.INDEX}
            className="DivisionVehicles-breadcrumb-link"
            onClick={this.handleSelectCategoryClick}
          >
            {UI.BREADCRUMB_SELECT_CATEGORY}
          </Link>
          <span className="DivisionVehicles-breadcrumb-separator" aria-hidden="true" />
          <span className="DivisionVehicles-breadcrumb-current">{division.title}</span>
        </>
      )
    }

    return (
      <>
        <Link
          to={ROUTE.INDEX}
          className="DivisionVehicles-breadcrumb-link"
          onClick={this.handleSelectCategoryClick}
        >
          {UI.BREADCRUMB_SELECT_CATEGORY}
        </Link>
        <span className="DivisionVehicles-breadcrumb-separator" aria-hidden="true" />
        <Link to={divisionPath} className="DivisionVehicles-breadcrumb-link">
          {division.title}
        </Link>
        <span className="DivisionVehicles-breadcrumb-separator" aria-hidden="true" />
        <span className="DivisionVehicles-breadcrumb-current">
          {PRODUCT_MAP[productType].title}
        </span>
      </>
    )
  }

  handleLogoClick = () => {
    navigateUnifiedHeaderLogo(this.props.history, isOffline())
  }

  render() {
    const division = this.division
    const { isDesktop, heroShotMap, vehicle: { currentYear, currentProductType, error } } = this.props
    const { setVehicleYear } = this.props.VehicleActions
    const { getHeroShot } = this.props.ContentsActions
    const { vehiclesReady, years, groups, brandFilter } = this.state
    const productType = currentProductType
    const displayModels = this.getDisplayModels()
    const availableBrands = getUniqueBrandsFromVehicles(this.state.models)
    const searchPlaceholder = isDesktop
      ? UI.UNIFIED_SEARCH_STEP1_PLACEHOLDER
      : UI.UNIFIED_SEARCH_STEP1_PLACEHOLDER_MOBILE

    if (!division) {
      return (
        <ErrorMessage
          className="inverse"
          title={MESSAGE.ERROR_NOT_FOUND}
          retryAction={ROUTE.INDEX}
          retryTitle="Visit Home Page"
          message={MESSAGE.ERROR_NOT_FOUND_MESSAGE}
        />
      )
    }

    if (error) {
      return <ErrorMessage retryAction={() => this.updateVehiclesList()} />
    }

    if (!vehiclesReady) {
      return (
        <div className="DivisionVehicles theme-forest-river">
          <Loader className="inverse" />
        </div>
      )
    }

    return (
      <div className="DivisionVehicles theme-forest-river">
        <Helmet>
          <title>{`${division.title} | ${UI.UNIFIED_APP_TITLE}`}</title>
        </Helmet>

        <UnifiedHeader
          breadcrumb={this.renderBreadcrumb(division, productType)}
          mobileDropdown={buildDivisionMobileDropdown({
            division,
            productType,
            year: currentYear,
            baseRoute: this.baseRoute,
            onSelectCategoryClick: this.handleSelectCategoryClick,
          })}
          onLogoClick={this.handleLogoClick}
        />

        <Media type="desktop">
          <StickyBar className="sticky-vehicle-year-selector DivisionVehicles-yearBar">
            <VehicleYearSelector
              currentYear={currentYear}
              years={years}
              onSelected={setVehicleYear}
              slider={!isDesktop && years.length > 4}
              baseRoute={this.baseRoute}
              variant="unified"
            />
          </StickyBar>
        </Media>
        <Media type="mobile">
          <VehicleYearSelector
            currentYear={currentYear}
            years={years}
            onSelected={setVehicleYear}
            slider={!isDesktop && years.length > 4}
            baseRoute={this.baseRoute}
            variant="unified"
          />
        </Media>

        <main className="DivisionVehicles-content">
          {productType ? (
            <div className="DivisionVehicles-filters">
              <VehicleModelSearch
                vehicles={this.state.models}
                onSelect={this.navigateToVehicle}
                label={UI.UNIFIED_SEARCH_STEP2_LABEL}
                placeholder={searchPlaceholder}
                filterList
                onQueryChange={(query) => this.setState({ searchQuery: query })}
              />
              <BrandPillFilter
                label={UI.UNIFIED_FILTER_BY_BRAND}
                allLabel={UI.UNIFIED_FILTER_ALL_BRANDS}
                brands={availableBrands}
                currentBrand={brandFilter}
                onChange={(brand) => this.setState({ brandFilter: brand })}
              />
            </div>
          ) : (
            <>
              <VehicleModelSearch
                vehicles={this.getSearchPool()}
                onSelect={this.navigateToVehicle}
                label={UI.UNIFIED_SEARCH_STEP1_LABEL}
                placeholder={searchPlaceholder}
                filterList={false}
              />
              <h1 className="DivisionVehicles-heading">{UI.VEHICLES_SCREEN_HEAD_TITLE}</h1>
            </>
          )}

          {!_.isEmpty(groups) || !_.isEmpty(displayModels) ? (
            <VehicleList
              isDesktop={isDesktop}
              currentYear={currentYear}
              productType={productType}
              currentModel={this.props.vehicle.currentModel}
              currentBrand={this.props.vehicle.currentBrand}
              vehicles={displayModels}
              groups={groups}
              heroShotMap={heroShotMap}
              getHeroShot={getHeroShot}
              setVehicleModel={(model) => {
                this.props.VehicleActions.setVehicleBrand(model.make)
                this.updateAnalyticsContext(model)
              }}
              baseRoute={this.baseRoute}
              getModelLink={this.getModelLink}
              showBrandSubtitle={!!productType}
              hideSubtypeFilter
              listVariant="unified"
            />
          ) : (
            <Loader type="status" className={isDesktop ? 'inverse' : ''}>{MESSAGE.EMPTY_VEHICLES}</Loader>
          )}
        </main>
      </div>
    )
  }
}

function mapStateToProps({ vehicle, contents, user }) {
  const media = user && user.media

  return {
    vehicle,
    isDesktop: media ? media.isDesktop : true,
    heroShotMap: contents.heroShotMap,
  }
}

function mapDispatchToProps(dispatch) {
  return {
    VehicleActions: bindActionCreators(VehicleActions, dispatch),
    ContentsActions: bindActionCreators(ContentsActions, dispatch),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(DivisionVehicles)
